import { Select } from 'antd'
import { type FC, useId } from 'react'

import { MAX_EVERY, MAX_NAME, MAX_NOTE, MAX_SERIES_VISITS } from 'features/Series/series.constants'

import type { PersonOption } from '../../ProviderField'
import { SeriesGrid } from '../controls/SeriesGrid'
import { SeriesStepper } from '../controls/SeriesStepper'
import { UnitSegmented } from '../controls/UnitSegmented'
import { unitLabel } from '../seriesDates'
import { SERIES_TEXT_V2 } from '../seriesText'
import gridStyles from '../controls/seriesGrid.module.scss'
import styles from '../series.module.scss'
import { createGridRows } from './seriesCreate.model'
import type { SeriesCreate } from './useSeriesCreate'

type SeriesCreatePanelProps = {
  create: SeriesCreate
  doctorOptions: PersonOption[]
  problems: { id: number; title: string }[]
}

const NONE = 0

type MenuOption = { value: number; label: string; meta?: string }

const Chevron: FC = () => (
  <span className={styles.chev} aria-hidden='true'>
    ▾
  </span>
)

export const SeriesCreatePanel: FC<SeriesCreatePanelProps> = ({
  create,
  doctorOptions,
  problems,
}) => {
  const ids = useId()
  const { head } = create

  if (!create.ready) {
    return (
      <div className={styles.panel}>
        <p className={styles.hint}>{SERIES_TEXT_V2.choosePrompt}</p>
      </div>
    )
  }

  const templateOptions: MenuOption[] = [
    { value: NONE, label: SERIES_TEXT_V2.templateBuildHere },
    ...create.templates.map((t) => ({
      value: t.id,
      label: t.name,
      meta: SERIES_TEXT_V2.templateMeta(t.visits, unitLabel(t.every, t.unit)),
    })),
  ]
  const problemOptions: MenuOption[] = [
    { value: NONE, label: SERIES_TEXT_V2.problemNone },
    ...problems.map((p) => ({ value: p.id, label: p.title })),
  ]
  const issueValue = head.issueId ?? NONE
  // Nothing changes while the save runs: a refusal marks the rows that were sent.
  const busy = create.isSaving
  const cadence = unitLabel(head.every, head.unit)

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <div className={styles.fieldTemplate}>
          <label className={styles.label} htmlFor={`${ids}-template`}>
            {SERIES_TEXT_V2.templateLabel}
          </label>
          <Select<number>
            id={`${ids}-template`}
            className={styles.select}
            value={head.templateId ?? NONE}
            disabled={busy}
            showSearch={false}
            suffixIcon={<Chevron />}
            classNames={{ popup: { root: gridStyles.menu } }}
            options={templateOptions}
            optionRender={(option) => (
              <span className={styles.optionRow}>
                <span className={styles.optionLabel}>{option.data.label}</span>
                {option.data.meta && <span className={styles.optionMeta}>{option.data.meta}</span>}
              </span>
            )}
            onChange={(next) => create.pickTemplate(next === NONE ? null : next)}
          />
        </div>
        <div>
          <span className={styles.label}>{SERIES_TEXT_V2.visitsLabel}</span>
          <SeriesStepper
            value={head.visits}
            min={1}
            max={MAX_SERIES_VISITS}
            decLabel={`${SERIES_TEXT_V2.decrease} ${SERIES_TEXT_V2.visitsLabel.toLowerCase()}`}
            incLabel={`${SERIES_TEXT_V2.increase} ${SERIES_TEXT_V2.visitsLabel.toLowerCase()}`}
            disabled={busy}
            onChange={(visits) => create.setHead({ visits })}
          />
        </div>
        <div>
          <span className={styles.label}>{SERIES_TEXT_V2.cadenceLabel}</span>
          <div className={styles.cadence}>
            <span className={styles.every}>{SERIES_TEXT_V2.every}</span>
            <SeriesStepper
              value={head.every}
              min={1}
              max={MAX_EVERY}
              decLabel={`${SERIES_TEXT_V2.decrease} ${SERIES_TEXT_V2.cadenceLabel.toLowerCase()}`}
              incLabel={`${SERIES_TEXT_V2.increase} ${SERIES_TEXT_V2.cadenceLabel.toLowerCase()}`}
              disabled={busy}
              onChange={(every) => create.setHead({ every })}
            />
            <UnitSegmented
              value={head.unit}
              ariaLabel={SERIES_TEXT_V2.cadenceLabel}
              disabled={busy}
              onChange={(unit) => create.setHead({ unit })}
            />
          </div>
        </div>
        <div className={styles.fieldName}>
          <label className={styles.label} htmlFor={`${ids}-name`}>
            {SERIES_TEXT_V2.nameLabel}
          </label>
          <input
            id={`${ids}-name`}
            className={styles.input}
            type='text'
            maxLength={MAX_NAME}
            placeholder={SERIES_TEXT_V2.namePlaceholder}
            disabled={busy}
            value={head.name}
            onChange={(event) => create.setHead({ name: event.target.value })}
          />
        </div>
        <div className={styles.fieldNote}>
          <label className={styles.label} htmlFor={`${ids}-note`}>
            {SERIES_TEXT_V2.noteLabel}
          </label>
          <input
            id={`${ids}-note`}
            className={styles.input}
            type='text'
            placeholder={SERIES_TEXT_V2.notePlaceholder}
            disabled={busy}
            maxLength={MAX_NOTE}
            value={head.note}
            onChange={(event) => create.setHead({ note: event.target.value })}
          />
        </div>
        <div className={styles.fieldProblem}>
          <label className={styles.label} htmlFor={`${ids}-problem`}>
            {SERIES_TEXT_V2.problemLabel}
          </label>
          <Select<number>
            id={`${ids}-problem`}
            className={styles.select}
            value={issueValue}
            disabled={busy}
            showSearch={false}
            suffixIcon={<Chevron />}
            classNames={{ popup: { root: gridStyles.menu } }}
            options={problemOptions}
            optionRender={(option) => (
              <span className={styles.optionRow}>
                <span className={styles.optionCheck} aria-hidden='true'>
                  {option.value === issueValue ? '✓' : ''}
                </span>
                <span className={styles.optionLabel}>{option.data.label}</span>
              </span>
            )}
            onChange={(next) => create.setHead({ issueId: next === NONE ? null : next })}
          />
        </div>
      </div>

      <div className={styles.schedule}>
        <span className={styles.scheduleLabel}>{SERIES_TEXT_V2.scheduleLabel}</span>
        <SeriesGrid
          mode='create'
          rows={createGridRows(create)}
          doctorOptions={doctorOptions}
          onEdit={create.editRow}
          onRemove={create.removeRow}
        />
        <span className={styles.help}>{SERIES_TEXT_V2.helpCreate}</span>
      </div>

      <div className={styles.actions}>
        <button
          type='button'
          className={styles.btn}
          disabled={busy || !create.canAddVisit || create.isPreviewing}
          onClick={create.addVisit}>
          {SERIES_TEXT_V2.addVisit}
        </button>
        <button
          type='button'
          className={`${styles.btn} ${create.stale ? styles.stale : ''}`}
          disabled={busy || create.isPreviewing}
          onClick={create.regenerate}>
          {SERIES_TEXT_V2.regenerate}
        </button>
        <button
          type='button'
          className={styles.btn}
          disabled={busy || create.ownSteps.length === 0}
          onClick={create.applyBandToAll}>
          {SERIES_TEXT_V2.applyAll}
        </button>
        <span className={styles.note}>{SERIES_TEXT_V2.applyAllHint}</span>
        {create.stale ? (
          <span className={`${styles.note} ${styles.noteStale}`}>
            {SERIES_TEXT_V2.staleNote(head.visits, cadence)}
          </span>
        ) : (
          <span className={styles.note}>{SERIES_TEXT_V2.freshNote}</span>
        )}
      </div>
    </div>
  )
}
